export type EditablePhoto = {
  src: string;
  alt: string;
  caption: string;
  width?: number;
  height?: number;
  sizes?: string;
};

export type EditableBlock = {
  kind: 'figure' | 'masonry' | 'row';
  columns: 1 | 2 | 3 | 4 | 5;
  rowClass: string;
  photos: EditablePhoto[];
};

export function masonryCount(columns: number): 1 | 2 | 3 | 4 | 5 {
  if (columns === 1 || columns === 2 || columns === 3 || columns === 4 || columns === 5) return columns;
  return 3;
}

function escapeAttr(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
}

function childFigures(element: HTMLElement): HTMLElement[] {
  const nodes = element.querySelectorAll('figure');
  const figures: HTMLElement[] = [];
  for (let index = 0; index < nodes.length; index += 1) {
    const figure = nodes[index];
    if (figure.closest('.photo-masonry, .photo-row') === element) figures.push(figure);
  }
  return figures;
}

function childByTag(figure: HTMLElement, tag: string): HTMLElement | undefined {
  for (let index = 0; index < figure.children.length; index += 1) {
    const child = figure.children[index];
    if (child instanceof HTMLElement && child.tagName === tag) return child;
  }
  return undefined;
}

function readFigure(figure: HTMLElement): EditablePhoto | null {
  const img = childByTag(figure, 'IMG');
  const src = img?.getAttribute('src') || '';
  if (!img || !src) return null;
  const width = Number(img.getAttribute('width'));
  const height = Number(img.getAttribute('height'));
  const caption = childByTag(figure, 'FIGCAPTION');
  return {
    src,
    alt: img.getAttribute('alt') || '',
    caption: caption?.textContent || '',
    width: width || undefined,
    height: height || undefined,
    sizes: img.getAttribute('sizes') || undefined,
  };
}

export function readArticleBlock(element: HTMLElement): EditableBlock | null {
  if (element.classList.contains('photo-masonry')) {
    const match = element.className.match(/\bphoto-masonry-([1-5])\b/);
    return {
      kind: 'masonry',
      columns: masonryCount(Number(match?.[1])),
      rowClass: '',
      photos: childFigures(element).map(readFigure).filter((photo): photo is EditablePhoto => Boolean(photo)),
    };
  }
  if (element.classList.contains('photo-row')) {
    return {
      kind: 'row',
      columns: element.classList.contains('photo-row-3') ? 3 : 2,
      rowClass: element.getAttribute('class') || '',
      photos: childFigures(element).map(readFigure).filter((photo): photo is EditablePhoto => Boolean(photo)),
    };
  }
  if (element.tagName === 'FIGURE') {
    const photo = readFigure(element);
    if (!photo) return null;
    return { kind: 'figure', columns: 1, rowClass: '', photos: [photo] };
  }
  return null;
}

export function figureMarkup(photo: EditablePhoto): string {
  const caption = photo.caption.trim() ? `<figcaption>${escapeAttr(photo.caption.trim())}</figcaption>` : '';
  const sizes = photo.sizes ? ` sizes="${escapeAttr(photo.sizes)}"` : '';
  const width = photo.width || 1200;
  const height = photo.height || 800;
  return `<figure><img src="${escapeAttr(photo.src)}" alt="${escapeAttr(photo.alt.trim())}" width="${width}" height="${height}"${sizes}>${caption}</figure>`;
}

export function articleBlockHtml(block: EditableBlock): string {
  const photos = block.photos.filter((photo) => photo.src).slice(0, 12);
  if (!photos.length) return '';
  if (block.kind === 'row' && photos.length > 1 && photos.length <= 3) {
    const count = photos.length >= 3 ? 3 : 2;
    const equal = /\bphoto-row-equal\b/.test(block.rowClass) ? ' photo-row-equal' : '';
    return `<div class="photo-row photo-row-${count}${equal}">${photos.slice(0, count).map(figureMarkup).join('')}</div>`;
  }
  if (photos.length === 1 && (block.kind === 'row' || masonryCount(block.columns) === 1)) return figureMarkup(photos[0]);
  const columns = masonryCount(block.columns);
  return `<div class="photo-masonry photo-masonry-${columns}">${photos.map(figureMarkup).join('')}</div>`;
}
